// Arrays — full course content for the DSA Explanation Modal.
// Java-focused. Structured as a linear mini-course (sections), not a flat tab set,
// because there's enough material here that students need to move through it in order
// and collapse sections they've already covered.

import arraysDrills from './arraysDrills';
const drill = id => arraysDrills.find(d => d.id === id);

const arrays = {
  key: 'Arrays',
  tagline: 'Contiguous, index-addressable memory — the foundation every other pattern is built on top of.',

  sections: [
    {
      id: 'intro',
      title: 'Introduction to Arrays',
      type: 'text',
      body: [
        "An array is Java's most basic data structure: a fixed-size, ordered collection of elements of the same type, stored back-to-back in memory.",
        "Every value has a position called an index, starting at 0 — arr[0] is the first element, not the \"oneth.\" This zero-based counting trips up almost every beginner at least once.",
        "Arrays aren't just \"one more data structure to learn\" — they're the base layer nearly everything else in this course is built on. A HashMap is internally an array of buckets. An ArrayList is a resizable wrapper around a plain array. A binary heap is an array read as if it were a tree. Understanding arrays properly makes every pattern after this one easier.",
      ],
    },
    {
      id: 'why',
      title: 'Why Arrays?',
      type: 'text',
      body: [
        "Without arrays, storing 40 students' exam marks would mean 40 separate variables — marks1, marks2, ... marks40 — and 40 lines of near-identical code every time you wanted to process them.",
        "int[] marks = new int[40]; replaces all of that with one variable and lets you loop over the data instead of hand-writing repetitive code for every single item.",
        "The deeper reason arrays matter: because their elements sit contiguously in memory, the JVM can compute any element's exact address with simple arithmetic instead of searching for it. That's what makes arr[i] an O(1) operation regardless of how large the array is — and it's the property every later technique (two pointers, prefix sums, binary search) depends on.",
      ],
    },
    {
      id: 'memory',
      title: 'Memory Representation',
      type: 'memory-diagram',
      body: [
        "Java allocates one continuous block of memory for the entire array — not scattered pieces linked together. If arr starts at memory address 1000 and each int takes 4 bytes, arr[i] lives at exactly address 1000 + i × 4. No searching, just arithmetic.",
        "For arrays of objects (like String[]), the array itself stores references (pointers) to objects that can live anywhere on the heap — so an array of objects costs one extra indirection compared to an array of primitives like int[].",
      ],
      demo: { values: [90, 85, 76, 60, 99], baseAddress: 1000, bytesPerElement: 4 },
    },
    {
      id: 'indexing',
      title: 'Indexing',
      type: 'text',
      body: [
        "Indexes start at 0 and go up to length − 1. For an array of 5 elements, valid indexes are 0, 1, 2, 3, 4 — never 5.",
        "arr[arr.length] is out of bounds and throws ArrayIndexOutOfBoundsException immediately. This single mistake — using <= instead of < in a loop condition — is the most common beginner crash in this entire topic.",
        "Unlike Python, Java has no negative indexing. arr[-1] doesn't mean \"last element\" — it throws an exception the same as any other out-of-range index.",
      ],
    },
    {
      id: 'declare',
      title: 'Declaring & Creating Arrays in Java',
      type: 'code',
      code:
`// Declare + create in one line (most common), all slots default to 0
int[] marks = new int[5];

// Declare + create with initial values directly
int[] scores = {90, 85, 76, 60, 99};

// Two-step: declare first, create later
int[] ages;
ages = new int[3];

// Array of objects — default value is null, NOT an empty value
String[] names = new String[3];`,
      note: 'Default values matter: numeric types default to 0, boolean defaults to false, and object-type arrays (String[], custom classes) default every slot to null — not an empty object.',
    },
    {
      id: 'access',
      title: 'Accessing Elements',
      type: 'code',
      code:
`int[] marks = {90, 85, 76, 60, 99};

int first = marks[0];   // 90
int third = marks[2];   // 76
int last  = marks[marks.length - 1]; // 99 — safest way to get the last element`,
      note: 'marks[marks.length - 1] always gets the last element correctly, even if you resize the array later — hardcoding marks[4] breaks the moment the size changes.',
    },
    {
      id: 'update',
      title: 'Updating Elements',
      type: 'code',
      code:
`int[] marks = {90, 85, 76, 60, 99};

marks[2] = 100;                 // overwrite index 2: 76 → 100
marks[marks.length - 1] += 5;   // update the last element relative to itself: 99 → 104`,
      note: "Assigning to an index overwrites a slot that already exists — it never inserts a new one and never changes the array's length.",
    },
    {
      id: 'length',
      title: 'Array Length',
      type: 'code',
      code:
`int[] marks = new int[5];
System.out.println(marks.length);   // 5 — a FIELD, no parentheses

String s = "hello";
System.out.println(s.length());     // String uses a METHOD instead

List<Integer> list = new ArrayList<>();
System.out.println(list.size());    // Collections use size(), yet another API`,
      note: 'Three different APIs for "how many": arr.length (field), str.length() (method), list.size() (method). Mixing these up is a genuinely common compile error, not just a beginner slip.',
    },
    {
      id: 'traverse',
      title: 'Traversing Arrays',
      type: 'code',
      code:
`// Index-based loop — use this when you need the position i itself
for (int i = 0; i < marks.length; i++) {
    System.out.println("Index " + i + ": " + marks[i]);
}

// Enhanced for-each loop — use this when you only need the values
for (int mark : marks) {
    System.out.println(mark);
}`,
      note: "for-each reads cleaner but can't tell you the index and can't modify the original array — 'mark' is a copy of the value, not a reference into the array. To update elements in place, you need the index-based loop.",
    },
    {
      id: 'scanner',
      title: 'User Input with Scanner',
      type: 'code',
      code:
`import java.util.Scanner;

Scanner sc = new Scanner(System.in);
int n = sc.nextInt();        // how many elements are coming
int[] arr = new int[n];
for (int i = 0; i < n; i++) {
    arr[i] = sc.nextInt();   // read each value one at a time
}
sc.close();`,
      note: 'This exact pattern — read N, then read N values — is how almost every TCS/Infosys online-test question hands you input. Memorize this block; you will type it from memory dozens of times.',
    },
    {
      id: 'errors',
      title: 'Common Errors',
      type: 'errors',
      items: [
        {
          mistake: 'ArrayIndexOutOfBoundsException from a loop bound',
          why: 'Looping with i <= arr.length instead of i < arr.length walks one step past the last valid index.',
          fix: 'Always use < with arr.length, never <=. Valid indexes run from 0 to length − 1.',
        },
        {
          mistake: 'NullPointerException on an object array',
          why: 'String[] names = new String[3]; leaves every slot as null (not "") — calling names[0].length() crashes before you ever assign a value.',
          fix: 'Assign a real value to each slot before calling methods on it, or null-check first: if (names[0] != null) { ... }',
        },
        {
          mistake: 'Mixing up .length and .length()',
          why: 'arr.length() is a compile error (arrays have no such method); str.length is also a compile error (String has no such field).',
          fix: 'Arrays use the field .length. String uses the method .length(). There is no way around memorizing this pair.',
        },
        {
          mistake: 'Off-by-one when reading Scanner input',
          why: 'Declaring int[] arr = new int[n] and then looping for (int i = 1; i <= n; i++) both skips index 0 and throws out-of-bounds the moment i reaches n.',
          fix: 'Loop i from 0 to n − 1 (i.e. i < n), matching the array\'s actual valid index range.',
        },
        {
          mistake: 'Expecting an array to resize itself',
          why: 'arr[10] = 5 on a length-5 array does not grow the array — it throws ArrayIndexOutOfBoundsException immediately.',
          fix: 'Java arrays are fixed-size forever after creation. If the size needs to grow, use ArrayList instead, or create a bigger array and copy the old values with Arrays.copyOf().',
        },
      ],
    },
    {
      id: 'complexity',
      title: 'Time Complexity',
      type: 'complexity',
      rows: [
        { op: 'Access by index — arr[i]', time: 'O(1)', note: 'Direct address arithmetic, no searching.' },
        { op: 'Search, unsorted array', time: 'O(n)', note: 'Worst case, every element must be checked.' },
        { op: 'Search, sorted array (binary search)', time: 'O(log n)', note: 'Each comparison halves the remaining range.' },
        { op: 'Insert / delete at the end (ArrayList)', time: 'O(1) amortized', note: 'No shifting required.' },
        { op: 'Insert / delete at the start or middle', time: 'O(n)', note: 'Every following element must shift by one.' },
        { op: 'Traverse the entire array', time: 'O(n)', note: 'Every element is visited exactly once.' },
      ],
    },
    {
      id: 'problems',
      title: 'Beginner Problems (Easy → Medium → Advanced)',
      type: 'problems',
      groups: [
        {
          level: 'Easy',
          items: [
            drill('find-largest-element'),
            drill('find-sum-average'),
            drill('check-array-sorted'),
            drill('reverse-array-in-place'),
            drill('count-occurrences'),
          ],
        },
        {
          level: 'Medium',
          items: [
            drill('second-largest-no-sort'),
            drill('rotate-array-left'),
            drill('move-zeros-to-end'),
            drill('find-missing-number'),
          ],
        },
        {
          level: 'Advanced',
          items: [
            drill('kadane-max-subarray-drill'),
            drill('find-leaders'),
            drill('alternate-positive-negative'),
          ],
        },
      ],
      note: 'These are foundation drills to build comfort with index logic before moving into the curated interview problem list for this pattern.',
    },
    {
      id: 'cheatsheet',
      title: 'Cheatsheet & Recap',
      type: 'cheatsheet',
      items: [
        'Zero-indexed: valid range is 0 to length − 1, never length itself.',
        'arr.length is a field (no parentheses) — String uses .length(), collections use .size().',
        'Fixed size forever — Java arrays never grow; use ArrayList if the size must change.',
        'Access is O(1). Insert/delete at the end is O(1) amortized (ArrayList). Insert/delete elsewhere is O(n).',
        'Index-based for-loop when you need i or must modify elements; for-each when you only need the values.',
        'Scanner pattern: read n, then loop n times reading one value each — the standard TCS/Infosys input format.',
        'Object-type arrays default every slot to null, not an empty value — check before calling methods on elements.',
      ],
    },
    {
      id: 'playground',
      title: 'Interactive Practice & Quiz',
      type: 'playground',
    },
  ],
};

export default arrays;
